// P-03 rules (UserFlows P-03, Security §8.2, D-74). The server re-checks everything (API.md §4.5); these only
// decide when Continue is enabled and when "Please use letters only" shows.

export interface IdentifyFields {
  first: string
  last: string
  mm: string
  dd: string
  yyyy: string
}

/** Characters a name may hold: letters (incl. accented), marks, space, hyphen, apostrophes (straight or curly). */
const ALLOWED_CHARS = /^[\p{Script=Latin}\p{M} '’‘ʼ-]*$/u
/** The server's rule after cleaning (Security §8.2). */
const NAME_RE = /^[\p{Script=Latin}\p{M}]+(?:[ '-][\p{Script=Latin}\p{M}]+)*$/u

/** Security §8.2 cleaning: NFC, curly apostrophes → `'`, trimmed, runs of spaces collapsed. */
export function cleanName(value: string): string {
  return value.normalize('NFC').replace(/[’‘ʼ]/g, "'").trim().replace(/ {2,}/g, ' ')
}

/** A character outside letters, space, hyphen and apostrophe: the inline "Please use letters only" (Q-3). */
export function nameHasInvalidChars(value: string): boolean {
  return !ALLOWED_CHARS.test(value)
}

/** Non-blank, 1–50 characters after cleaning, and the server's pattern. */
export function nameIsValid(value: string): boolean {
  const cleaned = cleanName(value)
  return cleaned.length >= 1 && cleaned.length <= 50 && NAME_RE.test(cleaned)
}

/** "Today" in the clinic's time zone, as `YYYY-MM-DD` (D-21). */
export function clinicToday(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** `YYYY-MM-DD` from the three boxes. */
export const dobOf = (f: Pick<IdentifyFields, 'mm' | 'dd' | 'yyyy'>) => `${f.yyyy}-${f.mm}-${f.dd}`

/** A real calendar date, in the past (clinic time zone), with a year after 1900; all three boxes filled. */
export function dobIsValid(f: Pick<IdentifyFields, 'mm' | 'dd' | 'yyyy'>, timeZone: string, now?: Date): boolean {
  if (f.mm.length !== 2 || f.dd.length !== 2 || f.yyyy.length !== 4) return false
  const [y, m, d] = [Number(f.yyyy), Number(f.mm), Number(f.dd)]
  const date = new Date(Date.UTC(y, m - 1, d))
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return false
  return y > 1900 && dobOf(f) < clinicToday(timeZone, now)
}

export const EMPTY_FIELDS: IdentifyFields = { first: '', last: '', mm: '', dd: '', yyyy: '' }
